import { grid } from "./grid.js";
import { store } from "../core/store.js";
import { world } from "./world.js";
import { sceneStates } from "../tools/sceneStates.js";

class WorldAPI {
  constructor() {
    this.subscribers = new Set();
    this.setupGridSubscription();
  }

  setupGridSubscription() {
    grid.subscribe((change) => {
      this.notifySubscribers({
        type: "grid",
        changeType: change.type,
        state: this.getGridState(),
      });
    });
  }

  getGridState() {
    try {
      return {
        visible: grid.getState().visible,
        colorMode: grid.getState().colorMode,
        scale: grid.getState().scale,
        tiling: grid.getState().tiling,
      };
    } catch (error) {
      console.warn("Error getting grid state:", error);
      return {
        visible: true,
        colorMode: "light",
        scale: 1.0,
        tiling: 500,
      };
    }
  }

  setGridColorMode(mode) {
    if (mode !== "light" && mode !== "dark") {
      console.warn("Invalid color mode:", mode);
      return false;
    }

    try {
      world.public.setGridLight = function () {
        grid.setColorMode("light");
        store.requestRender();
      };

      world.public.setGridDark = function () {
        grid.setColorMode("dark");
        store.requestRender();
      };

      if (mode === "light") {
        world.public.setGridLight();
      } else {
        world.public.setGridDark();
      }
      return true;
    } catch (error) {
      console.error("Error setting grid color mode:", error);
      return false;
    }
  }

  setGridVisible(visible) {
    try {
      world.public.setGridVisible(visible);
      sceneStates.public.captureCurrentWorldState();

      if (typeof store.setUnsavedScene === "function") {
        store.setUnsavedScene();
      }

      return true;
    } catch (error) {
      console.error("Error setting grid visibility:", error);
      return false;
    }
  }

  setGridScale(scale) {
    try {
      world.public.setScale(scale);
      return true;
    } catch (error) {
      console.error("Error setting grid scale:", error);
      return false;
    }
  }

  setGridTiling(tiling) {
    try {
      world.public.setGridTextureRepeat(tiling);
      return true;
    } catch (error) {
      console.error("Error setting grid tiling:", error);
      return false;
    }
  }

  getGlobalTransform() {
    try {
      if (window.Inventum && window.Inventum.settings) {
        return window.Inventum.settings.getGlobalTransform();
      }
      return { x: 0, y: 0, z: 0 };
    } catch (error) {
      console.warn("Error getting global transform:", error);
      return { x: 0, y: 0, z: 0 };
    }
  }

  setGlobalTransform(transform) {
    try {
      if (window.Inventum && window.Inventum.settings) {
        window.Inventum.settings.setGlobalTransform(transform);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error setting global transform:", error);
      return false;
    }
  }

  getGlobalScale() {
    try {
      if (window.Inventum && window.Inventum.settings) {
        return window.Inventum.settings.getGlobalScale();
      }
      return 1.0;
    } catch (error) {
      console.warn("Error getting global scale:", error);
      return 1.0;
    }
  }

  setGlobalScale(scale) {
    try {
      if (window.Inventum && window.Inventum.settings) {
        window.Inventum.settings.setGlobalScale(scale);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error setting global scale:", error);
      return false;
    }
  }

  getSceneFOV() {
    try {
      if (window.Inventum && window.Inventum.camera) {
        return window.Inventum.camera.getSceneFOV();
      }
      return 75;
    } catch (error) {
      console.warn("Error getting scene FOV:", error);
      return 75;
    }
  }

  setSceneFOV(fov) {
    try {
      if (window.Inventum && window.Inventum.camera) {
        window.Inventum.camera.setSceneFOV(fov);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error setting scene FOV:", error);
      return false;
    }
  }

  getAllSettings() {
    return {
      grid: this.getGridState(),
      transform: this.getGlobalTransform(),
      globalScale: this.getGlobalScale(),
      sceneFOV: this.getSceneFOV(),
    };
  }

  subscribe(callback) {
    if (typeof callback === "function") {
      this.subscribers.add(callback);
      return () => this.subscribers.delete(callback);
    }
  }

  notifySubscribers(data) {
    this.subscribers.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error("Error in worldAPI subscriber:", error);
      }
    });
  }

  dispose() {
    this.subscribers.clear();
  }
}

export const worldAPI = new WorldAPI();
